import { Injectable } from '@angular/core';
import { where } from '@angular/fire/firestore';
import { Club } from '@models';
import { Movement, MovementType } from '@models';
import { Observable, combineLatest, map, shareReplay, switchMap } from 'rxjs';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { TenantContextService } from './tenant-context.service';

@Injectable({
  providedIn: 'root',
})
export class MovementService {
  public all$: Observable<Movement[]>;

  public incomeMovements$: Observable<(Movement & { club?: Club })[]>;

  constructor(
    private firestore: FirestoreService,
    private tenantContext: TenantContextService,
    private seasonManager: SeasonManagerService,
    private clubsService: ClubsService,
  ) {
    this.all$ = this.seasonManager.season$.pipe(
      switchMap((season) =>
        this.firestore.listDocuments<Movement>(
          this.tenantContext.collections.MOVEMENTS,
          where('seasonYear', '==', season),
        ),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.incomeMovements$ = combineLatest([
      this.all$.pipe(
        map((movements) =>
          movements
            .filter((m) => m.movementType === MovementType.INCOME)
            .sort((a, b) => {
              const dateA = a.date
                ? new Date(a.date)
                : new Date(a.createdAt ?? 0);
              const dateB = b.date
                ? new Date(b.date)
                : new Date(b.createdAt ?? 0);
              return dateB.getTime() - dateA.getTime();
            }),
        ),
      ),
      this.clubsService.all$,
    ]).pipe(
      map(([movements, clubs]) =>
        movements.map((movement) => ({
          ...movement,
          club: movement.clubId
            ? clubs.find((club) => club.id === movement.clubId)
            : undefined,
        })),
      ),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  create(movement: Omit<Movement, 'id'>) {
    return this.firestore.create<Movement>(
      this.tenantContext.collections.MOVEMENTS,
      movement,
    );
  }

  update(id: string, movement: Partial<Movement>) {
    return this.firestore.update<Movement>(
      this.tenantContext.collections.MOVEMENTS,
      { ...movement, id },
    );
  }

  delete(id: string) {
    return this.firestore.deleteForever(
      this.tenantContext.collections.MOVEMENTS,
      id,
    );
  }

  get(id: string) {
    return this.firestore.getDocument<Movement>(
      this.tenantContext.collections.MOVEMENTS,
      id,
    );
  }
}
